import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { requestorReview, auth } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck, CheckCircle2, XCircle, Loader2, Inbox, AlertTriangle, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import PRReviewDetailDrawer from "@/components/PRReviewDetailDrawer";
import UserMenu from "@/components/UserMenu";

const REJECT_REASONS = ["Price unacceptable", "Not Required", "Quantity is too high"];

const FLAG_ENABLED = process.env.REACT_APP_ENABLE_REQUESTOR_REVIEW === "true";

function fmtAmt(n) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(n || 0));
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function PRReview() {
  const me = auth.user();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // voc_no being acted on
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState(null); // {voc_no, supplier, amount}
  const [rejectReason, setRejectReason] = useState("Price unacceptable");
  const [drawerFor, setDrawerFor] = useState(null); // row currently shown in detail drawer

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await requestorReview.myPRs();
      setRows(data || []);
    } catch (e) {
      console.error("review fetch failed", e);
      const msg = e?.response?.data?.detail || "Failed to load review queue";
      if (e?.response?.status === 404) {
        toast.error("Requestor Review is disabled on this environment");
      } else {
        toast.error(msg);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (FLAG_ENABLED) load(); }, [load]);

  const doApprove = async (row) => {
    setActing(row.voc_no);
    // Optimistic: mark the row Approved immediately so it moves from
    // "Pending your review" to "Already reviewed" without waiting for the
    // backend. Format matches what MS SQL writes on the server side.
    const ts = new Date().toISOString().slice(0, 16).replace("T", " ");
    const optimisticRemark = `Approve · ${ts}`;
    const prevRows = rows;
    setRows((prev) => prev.map((r) =>
      r.voc_no === row.voc_no
        ? { ...r, review_state: "Approved", requester_remark: optimisticRemark }
        : r
    ));
    setDrawerFor(null);
    try {
      await requestorReview.decide(row.voc_no, "Approve");
      toast.success(`PR #${row.voc_no} approved`);
      // No load() — the local state already reflects the change. Backend has
      // patched the mirror in-place; next natural navigation refreshes anyway.
    } catch (e) {
      // Revert on failure.
      setRows(prevRows);
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setActing(null); }
  };

  const openReject = (row) => {
    setRejectFor(row);
    setRejectReason("Price unacceptable");
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectFor) return;
    setActing(rejectFor.voc_no);
    // Optimistic: mark rejected locally and stamp the reason.
    const ts = new Date().toISOString().slice(0, 16).replace("T", " ");
    const optimisticRemark = `Reject · ${rejectReason} · ${ts}`;
    const target = rejectFor.voc_no;
    const prevRows = rows;
    setRows((prev) => prev.map((r) =>
      r.voc_no === target
        ? { ...r, review_state: "Rejected", requester_remark: optimisticRemark }
        : r
    ));
    setRejectOpen(false);
    setRejectFor(null);
    setDrawerFor(null);
    try {
      await requestorReview.decide(target, "Reject", rejectReason);
      toast.success(`PR #${target} rejected · ${rejectReason}`);
    } catch (e) {
      setRows(prevRows);
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setActing(null); }
  };

  // Feature flag OFF -> safety message (this page shouldn't even be routed but guard anyway)
  if (!FLAG_ENABLED) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center p-8 bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="review-disabled">
          <AlertTriangle className="mx-auto text-amber-500 mb-3" size={32} />
          <h2 className="text-lg font-bold text-slate-800">Requestor Review is not enabled</h2>
          <p className="text-sm text-slate-600 mt-2">
            This feature is gated by the <code className="px-1 py-0.5 bg-slate-100 rounded">ENABLE_REQUESTOR_REVIEW</code> flag.
          </p>
          <Button className="mt-5" onClick={() => nav("/home")} data-testid="review-back-home">
            <ArrowLeft size={14} className="mr-1" /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const pending = rows.filter((r) => r.review_state === "Pending");
  const decided = rows.filter((r) => r.review_state !== "Pending");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-14 bg-cyan-700 text-white sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav("/home")} className="text-white hover:bg-white/10" data-testid="review-home">
              <ArrowLeft size={16} className="mr-1" /> Home
            </Button>
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} />
              <div className="text-sm sm:text-base font-bold tracking-tight">Verify My PRs</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <UserMenu tone="dark" />
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Verify PRs raised in your name</h1>
          <p className="text-sm text-slate-600 mt-1">
            Confirm each PR is genuine. <strong>Reject</strong> any you don&apos;t recognise — it will be blocked from the Authoriser queue.
          </p>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center" data-testid="review-loading">
            <Loader2 className="animate-spin" size={16} /> Loading your PRs…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-10 text-center" data-testid="review-empty">
            <Inbox className="mx-auto text-slate-400 mb-3" size={32} />
            <div className="text-sm text-slate-600">Nothing to review — no Open PRs are currently in your name.</div>
          </div>
        )}

        {!loading && pending.length > 0 && (
          <section className="mb-8">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Pending your review</h2>
              <span className="text-xs text-slate-500" data-testid="review-pending-count">{pending.length} PR{pending.length === 1 ? "" : "s"}</span>
            </div>
            <div className="space-y-2">
              {pending.map((r) => (
                <PRCard
                  key={r.voc_no}
                  row={r}
                  acting={acting === r.voc_no}
                  meEmpNo={me?.emp_no || ""}
                  onView={() => setDrawerFor(r)}
                  onApprove={() => doApprove(r)}
                  onReject={() => openReject(r)}
                />
              ))}
            </div>
          </section>
        )}

        {!loading && decided.length > 0 && (
          <section>
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Already reviewed</h2>
              <span className="text-xs text-slate-500">{decided.length}</span>
            </div>
            <div className="space-y-2">
              {decided.map((r) => (
                <DecidedCard key={r.voc_no} row={r} onView={() => setDrawerFor(r)} />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* PR detail drawer (click on any card to open) */}
      <PRReviewDetailDrawer
        vocNo={drawerFor?.voc_no}
        open={!!drawerFor}
        onClose={() => setDrawerFor(null)}
        reviewState={drawerFor?.review_state}
        requesterRemark={drawerFor?.requester_remark}
        prRemark={drawerFor?.pr_remark}
        acting={acting === drawerFor?.voc_no}
        onApprove={() => drawerFor && doApprove(drawerFor)}
        onReject={() => drawerFor && openReject(drawerFor)}
      />

      {/* Reject reason dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject PR #{rejectFor?.voc_no}</DialogTitle>
            <DialogDescription>
              Pick the reason — once you confirm, this PR will not move forward for procurement.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <RadioGroup value={rejectReason} onValueChange={setRejectReason} className="space-y-2">
              {REJECT_REASONS.map((reason) => (
                <div key={reason} className="flex items-center space-x-2 border border-slate-200 rounded px-3 py-2 hover:border-rose-300 cursor-pointer">
                  <RadioGroupItem value={reason} id={`reason-${reason}`} data-testid={`reject-reason-${reason.toLowerCase().replace(/\s+/g, "-")}`} />
                  <Label htmlFor={`reason-${reason}`} className="text-sm cursor-pointer flex-1">{reason}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} data-testid="reject-cancel">Cancel</Button>
            <Button
              onClick={confirmReject}
              disabled={acting === rejectFor?.voc_no}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="reject-confirm"
            >
              {acting === rejectFor?.voc_no ? <Loader2 className="animate-spin" size={14} /> : "Confirm Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PRCard({ row, acting, meEmpNo, onView, onApprove, onReject }) {
  const stop = (e) => e.stopPropagation();
  // Only the original CREATOR can edit a PR. The Requestor (who is reviewing
  // here) may simply Approve or Reject. If the requestor also happens to be
  // the creator (i.e. raised the PR in their own name), Edit shows.
  const canEdit = !!(meEmpNo && row?.creator?.emp_no && row.creator.emp_no === meEmpNo);
  return (
    <div
      className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:border-cyan-500 hover:shadow-sm cursor-pointer transition-colors"
      data-testid={`pr-card-${row.voc_no}`}
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onView(); }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-slate-900">PR #{row.voc_no}</span>
          <span className="text-xs px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600 uppercase tracking-wider font-semibold">{row.purc_type}</span>
          <span className="text-xs text-slate-500">{fmtDate(row.vdate)}</span>
          <span className="text-[10px] text-cyan-700 font-semibold uppercase tracking-wider ml-auto sm:ml-0">View details →</span>
        </div>
        <div className="text-sm text-slate-700 mt-1 truncate">
          {row.supplier || row.pcode || "—"}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {row.currency !== "INR" ? (
            <>{row.currency} {fmtAmt(row.amount / (row.exch_rate || 1))} · ≈ ₹{fmtAmt(row.amount)}</>
          ) : (
            <>₹{fmtAmt(row.amount)}</>
          )}
        </div>
        {row.pr_remark && (
          <div
            className="mt-2 inline-flex items-start gap-1.5 max-w-full text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1"
            data-testid={`pr-remark-${row.voc_no}`}
            title={row.pr_remark}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 flex-shrink-0">Remark</span>
            <span className="truncate">{row.pr_remark}</span>
          </div>
        )}
      </div>
      <div className="flex gap-2 sm:flex-shrink-0" onClick={stop}>
        {canEdit && (
          <Button
            onClick={(e) => { e.stopPropagation(); window.location.href = `/pr/edit/${row.voc_no}`; }}
            disabled={acting}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid={`edit-${row.voc_no}`}
            title="Edit this PR before approving"
          >
            <Pencil size={14} className="mr-1" /> Edit
          </Button>
        )}
        <Button
          onClick={onApprove}
          disabled={acting}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`approve-${row.voc_no}`}
        >
          {acting ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle2 size={14} className="mr-1" /> Approve</>}
        </Button>
        <Button
          onClick={onReject}
          disabled={acting}
          variant="outline"
          className="border-rose-300 text-rose-700 hover:bg-rose-50"
          data-testid={`reject-${row.voc_no}`}
        >
          <XCircle size={14} className="mr-1" /> Reject
        </Button>
      </div>
    </div>
  );
}

function DecidedCard({ row, onView }) {
  const isApprove = row.review_state === "Approved";
  return (
    <div
      className="bg-slate-50/50 border border-slate-200 rounded-sm p-3 flex items-center gap-3 cursor-pointer hover:border-slate-300 hover:bg-slate-50"
      data-testid={`pr-decided-${row.voc_no}`}
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onView(); }}
    >
      <div className={`w-1 self-stretch rounded-full ${isApprove ? "bg-emerald-500" : "bg-rose-500"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-800 text-sm">PR #{row.voc_no}</span>
          <span className="text-[11px] text-slate-500">{fmtDate(row.vdate)}</span>
          <span className="text-[11px] text-slate-500 truncate">{row.supplier}</span>
          <span className="text-[10px] text-cyan-700 font-semibold uppercase tracking-wider ml-auto">View →</span>
        </div>
        <div className={`text-xs mt-0.5 ${isApprove ? "text-emerald-700" : "text-rose-700"}`}>
          {row.requester_remark}
        </div>
      </div>
    </div>
  );
}
